import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TrendingUp, Check, X, Repeat, Calculator, AlertTriangle, PauseCircle, Pencil } from 'lucide-react';
import api from '../../utils/api';
import InputMonto from '../../components/InputMonto';
import { money, moneyK, Kpi, Title, Badge, Empty } from './ui';

const ESTADO = {
  activo: { label: 'Activo', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  pausado: { label: 'Pausado', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  baja: { label: 'Baja', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
};

/* En qué anda cada abono este mes. El orden en que se evalúan está en el
   backend; acá solo se pintan. */
const ESTADO_MES = {
  al_dia:        { label: 'Al día',        color: '#64748b', bg: 'rgba(100,116,139,0.14)' },
  enviar_reporte:{ label: 'Enviar reporte',color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  emitir_cobro:  { label: 'Emitir cobro',  color: '#f59e0b', bg: 'rgba(245,158,11,0.16)' },
  falta_avisar:  { label: 'Falta avisar',  color: '#f97316', bg: 'rgba(249,115,22,0.16)' },
  cobro_emitido: { label: 'Cobro emitido', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
  pagado:        { label: 'Pagado',        color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  pausado:       { label: 'En pausa',      color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  baja:          { label: 'Baja',          color: '#64748b', bg: 'rgba(100,116,139,0.14)' },
};

export default function AgenciaAbonos() {
  const [data, setData] = useState(null);
  const [editando, setEditando] = useState(null);   // id del contrato en edición
  const [valor, setValor] = useState('');
  const [pct, setPct] = useState('');
  const [filtro, setFiltro] = useState('activo');
  const [diaEditando, setDiaEditando] = useState(null);
  const navigate = useNavigate();

  const cargar = () => api.get('/agency/abonos').then(r => setData(r.data)).catch(() => toast.error('No se pudo cargar'));
  useEffect(() => { cargar(); }, []);

  const abrir = (a) => { setEditando(a.id); setValor(String(Math.round(Number(a.monto)))); setPct(''); };
  const aplicarPct = (a, p) => {
    setPct(p);
    const n = Number(p);
    if (!isNaN(n) && n) setValor(String(Math.round(Number(a.monto) * (1 + n / 100))));
  };
  // El aviso de cobro puede salir solo o esperar aprobación en Envíos
  const alternarAviso = async (a, on) => {
    const pintar = (v) => setData(d => (d ? { ...d, abonos: d.abonos.map(x => (x.id === a.id ? { ...x, aviso_automatico: v } : x)) } : d));
    pintar(on ? 1 : 0);
    try {
      await api.put(`/agency/contratos/${a.id}/aviso`, { aviso_automatico: on });
      toast.success(on ? `El aviso de ${a.cliente} sale automático` : `El aviso de ${a.cliente} va a esperar tu ok`);
    } catch {
      pintar(a.aviso_automatico);
      toast.error('No se pudo cambiar');
    }
  };
  /* Mes vencido: el cobro de octubre corresponde al servicio de septiembre.
     Lo único que cambia es a qué mes se imputa en los reportes; las fechas, el
     aviso y la plata son exactamente iguales. */
  const alternarMesVencido = async (a, on) => {
    const pintar = (v) => setData(d => (d ? { ...d, abonos: d.abonos.map(x => (x.id === a.id ? { ...x, mes_vencido: v } : x)) } : d));
    pintar(on ? 1 : 0);
    try {
      await api.put('/agency/contratos/' + a.id, { mes_vencido: on ? 1 : 0 });
      toast.success(on ? `${a.cliente}: se contabiliza en el mes anterior` : `${a.cliente}: se contabiliza en el mes en curso`);
    } catch { pintar(a.mes_vencido); toast.error('No se pudo cambiar'); }
  };

  /* El día del mes en que se le emite el cobro. Se edita en la misma celda:
     cambiarlo no es raro (el cliente pide que le caiga después del 10) y no
     merece abrir una ficha entera para tocar un número. */
  const guardarDia = async (a, dia) => {
    const d = Math.max(1, Math.min(31, Number(dia) || 1));
    setDiaEditando(null);
    if (d === (a.dia_cobro || 1)) return;
    setData(x => ({ ...x, abonos: x.abonos.map(y => (y.id === a.id ? { ...y, dia_cobro: d } : y)) }));
    try {
      await api.put('/agency/contratos/' + a.id, { dia_cobro: d });
      toast.success(`${a.cliente}: se le cobra el ${d} de cada mes`);
      cargar();
    } catch { toast.error('No se pudo guardar'); cargar(); }
  };

  const cambiarEstado = async (a, estado) => {
    try {
      await api.put('/agency/contratos/' + a.id, { estado });
      toast.success(estado === 'activo' ? `${a.cliente}: abono retomado` : `${a.cliente}: abono en pausa`);
      cargar();
    } catch { toast.error('No se pudo cambiar'); }
  };
  const guardar = async (a) => {
    const monto = Number(valor);
    if (!monto || monto <= 0) return toast.error('Monto inválido');
    try {
      const { data: r } = await api.put(`/agency/contratos/${a.id}/precio`, { monto });
      toast.success(`${a.cliente}: ${money(r.anterior)} → ${money(r.nuevo)} (${r.porcentaje > 0 ? '+' : ''}${r.porcentaje}%)`);
      setEditando(null);
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); }
  };

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const abonos = data.abonos.filter(a => filtro === 'todos' || a.estado === filtro);
  const pendientesAjuste = data.abonos.filter(a => a.estado === 'activo' && (a.dias_sin_ajuste === null || a.dias_sin_ajuste >= 90));
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const chip = (activo) => ({
    padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: '1px solid ' + (activo ? 'var(--accent)' : 'var(--border)'),
    background: activo ? 'var(--accent)' : 'transparent', color: activo ? '#fff' : 'var(--text-secondary)',
  });

  return (
    <div>
      <Title>Abonos mensuales</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi icono={Repeat} label="Facturación mensual" value={moneyK(data.total_mensual)} sub={`${data.cantidad} abonos activos`} color="var(--accent)" />
        <Kpi icono={Calculator} label="Promedio por abono" value={moneyK(data.cantidad ? data.total_mensual / data.cantidad : 0)} />
        <Kpi icono={AlertTriangle} label="Sin ajustar hace 3+ meses" value={pendientesAjuste.length} color={pendientesAjuste.length ? '#f59e0b' : 'var(--text-primary)'} sub="Candidatos a aumento" />
        <Kpi icono={PauseCircle} label="En pausa" value={moneyK(data.abonos.filter(a => a.estado === 'pausado').reduce((x, a) => x + Number(a.monto || 0), 0))}
          sub={`${data.abonos.filter(a => a.estado === 'pausado').length} abonos para retomar`} color="#f59e0b"
          onClick={() => setFiltro('pausado')} />
        <Kpi icono={AlertTriangle} label="Con algo pendiente" value={data.por_hacer ?? 0}
          sub="reporte, cobro o aviso" color={data.por_hacer ? '#ef4444' : 'var(--text-primary)'}
          onClick={() => setFiltro('activo')} />
        <Kpi icono={TrendingUp} label="Proyección anual" value={moneyK(data.total_mensual * 12)} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {[['activo', 'Activos'], ['pausado', 'Pausados'], ['baja', 'Bajas'], ['todos', 'Todos']].map(([k, l]) => (
          <div key={k} style={chip(filtro === k)} onClick={() => setFiltro(k)}>{l}</div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Aumento asistido</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Ordenados por día de cobro, del 1 en adelante. Tocá el monto para actualizarlo: el aumento queda registrado y se aplica al próximo cobro.
        </div>
        {abonos.length ? (
          <div className="table-container" style={{ border: 0, borderRadius: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Cliente</th><th style={th}>Servicio</th>
                <th style={{ ...th, textAlign: 'center' }} title="Día del mes en que se le emite el cobro">Día</th>
                <th style={th}>Estado del mes</th><th style={{ ...th, textAlign: 'right' }}>Monto</th>
                <th style={{ ...th, textAlign: 'center' }} title="Si está prendido, el cobro se imputa al mes anterior: se cobra en octubre el servicio de septiembre">Mes vencido</th>
                <th style={{ ...th, textAlign: 'center' }} title="Si está prendido, el aviso de cobro le sale solo al cliente">Aviso automático</th>
                <th style={th}></th>
              </tr></thead>
              <tbody>{abonos.map(a => {
                const e = ESTADO[a.estado] || {};
                const em = ESTADO_MES[a.estado_mes] || ESTADO_MES.al_dia;
                const enEdicion = editando === a.id;
                return (
                  <tr key={a.id}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                        onClick={() => navigate(`/agencia/clientes/${a.cliente_id}`)}>
                        <span title={e.label} style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                          background: e.color || 'var(--text-muted)' }} />
                        <span style={{ fontWeight: 600 }}>{a.cliente}</span>
                      </div>
                    </td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{a.nombre || a.servicio || 'Abono'}</td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 600 }}>
                      {diaEditando === a.id ? (
                        <input type="number" min={1} max={31} autoFocus defaultValue={a.dia_cobro || 1}
                          onBlur={e => guardarDia(a, e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') guardarDia(a, e.target.value);
                            if (e.key === 'Escape') setDiaEditando(null);
                          }}
                          style={{ width: 62, textAlign: 'center', padding: '4px 6px', fontSize: 13 }} />
                      ) : (
                        <button className="celda-editable" title="Cambiar el día de cobro"
                          onClick={() => setDiaEditando(a.id)}>
                          {a.dia_cobro || 1}<Pencil size={11} />
                        </button>
                      )}
                    </td>
                    <td style={td}>
                      <Badge label={em.label} color={em.color} bg={em.bg} />
                      {/* El reporte se sigue marcando aunque el cobro ya haya salido:
                          es trabajo pendiente igual, solo que ya llegó tarde */}
                      {a.falta_reporte && a.estado_mes !== 'enviar_reporte' && a.estado === 'activo' && (
                        <div style={{ fontSize: 10.5, color: '#ef4444', marginTop: 3 }}>sin reporte de {a.periodo_reporte}</div>
                      )}
                      {a.estado_mes === 'al_dia' && a.dias_para_cobro > 0 && (
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
                          se cobra en {a.dias_para_cobro} día{a.dias_para_cobro === 1 ? '' : 's'}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }} data-monto>
                      {enEdicion ? (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                          {[10, 15, 20, 25].map(p => (
                            <span key={p} onClick={() => aplicarPct(a, p)}
                              style={{ ...chip(String(pct) === String(p)), padding: '3px 8px', fontSize: 11 }}>+{p}%</span>
                          ))}
                          <InputMonto autoFocus value={valor} onChange={v => { setValor(v); setPct(''); }}
                            onKeyDown={e2 => { if (e2.key === 'Enter') guardar(a); if (e2.key === 'Escape') setEditando(null); }}
                            style={{ width: 120, textAlign: 'right' }} />
                        </div>
                      ) : (
                        <span onClick={() => abrir(a)} style={{ fontWeight: 700, cursor: 'pointer', borderBottom: '1px dashed var(--border)' }}>{money(a.monto)}</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <label className="interruptor" title={a.mes_vencido
                        ? 'Se cobra a mes vencido: este cobro cuenta en el mes anterior'
                        : 'Se cobra el mes en curso'}>
                        <input type="checkbox" checked={!!a.mes_vencido}
                          onChange={e => alternarMesVencido(a, e.target.checked)} />
                        <span />
                      </label>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <label className="interruptor" title={a.aviso_automatico
                        ? 'El aviso de cobro le sale solo al cliente'
                        : 'El aviso queda esperando tu ok en Envíos'}>
                        <input type="checkbox" checked={!!a.aviso_automatico}
                          onChange={e => alternarAviso(a, e.target.checked)} />
                        <span />
                      </label>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {a.estado === 'pausado' && !enEdicion && (
                        <button className="btn btn-ghost btn-sm" title="Retomar el abono" onClick={() => cambiarEstado(a, 'activo')}
                          style={{ color: '#22c55e' }}>Retomar</button>
                      )}
                      {a.estado === 'activo' && !enEdicion && (
                        <button className="btn btn-ghost btn-sm" title="Pausar el abono" onClick={() => cambiarEstado(a, 'pausado')}
                          style={{ color: 'var(--text-muted)' }}>Pausar</button>
                      )}
                      {enEdicion ? (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => guardar(a)} title="Guardar"><Check size={16} color="#22c55e" /></button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditando(null)} title="Cancelar"><X size={16} /></button>
                        </>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => abrir(a)} title="Ajustar precio"><TrendingUp size={16} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        ) : <Empty>No hay abonos en este estado</Empty>}
      </div>
    </div>
  );
}
